import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import Navbar from './components/Navbar';
import SearchPage from './components/SearchPage';
import SqlResultsPage from './components/SqlResultsPage';
import AdminDashboard from './components/AdminDashboard';
import UserProfile from './components/UserProfile';
import './styles/variables.css';
import './styles/components.css';
import './App.css';

// Types
interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user_info: {
    username: string;
    email: string;
    user_attributes: any;
    groups?: string[];
  };
}

interface User {
  username: string;
  email: string;
  user_attributes: any;
  groups?: string[];
}

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
    }
  }, []);

  const handleLogin = async (username: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post<LoginResponse>(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      });

      const { access_token, user_info } = response.data;
      
      // Store auth data
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('userData', JSON.stringify(user_info));
      
      // Log user permissions
      console.log('=== USER PERMISSIONS ===');
      console.log('Username:', user_info.username);
      console.log('Email:', user_info.email);
      console.log('Groups:', user_info.groups || []);
      console.log('Attributes:', user_info.user_attributes);
      console.log('=======================');
      
      setUser(user_info);
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setUser(null);
  };

  if (!user) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src="/white_logo.png" alt="NextFed Intelligence" className="login-logo" />
            <h1>Sign in to your account</h1>
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <form className="login-form" onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const username = formData.get('username') as string;
            const password = formData.get('password') as string;
            handleLogin(username, password);
          }}>
            <div className="form-group">
              <label htmlFor="username" className="form-label">Email address</label>
              <input
                type="text"
                id="username"
                name="username"
                placeholder="Enter your email"
                required
                disabled={loading}
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <div className="label-row">
                <label htmlFor="password" className="form-label">Password</label>
                <a href="#" className="forgot-link">Forgot password?</a>
              </div>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                  className="form-input password-input"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            
            <div className="form-group remember-me">
              <input
                type="checkbox"
                id="remember"
                name="remember"
                className="checkbox"
              />
              <label htmlFor="remember" className="checkbox-label">Remember me</label>
            </div>
            
            <button type="submit" className="btn-signin" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
          
          <div className="login-footer">
            <p>Need assistance? <a href="#" className="signup-link">Contact administrator</a></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="app">
        <Navbar user={user} onLogout={handleLogout} />
        
        <main className="main-content">
          <Routes>
            <Route path="/" element={<SearchPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/sql-results" element={<SqlResultsPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/profile" element={<UserProfile />} />
            {/* Catch all route - redirect to search */}
            <Route path="*" element={<SearchPage />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
